import React from 'react';
import { motion } from 'framer-motion';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const HeroSection = ({ onBrowseWebinars, onBecomeInstructor }) => {
  return (
    <section className="relative min-h-screen overflow-hidden bg-white">
      {/* Diagonal Background Split */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-indigo-600 to-blue-700" 
             style={{ clipPath: 'polygon(0 0, 100% 0, 100% 70%, 0 100%)' }} 
        />
      </div>

      {/* Floating 3D Shapes */}
      <motion.div
        animate={{ 
          y: [0, -30, 0],
          rotate: [0, 180, 360]
        }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        className="absolute top-20 right-20 w-32 h-32 bg-blue-400/20 backdrop-blur-sm"
        style={{ 
          borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
          transform: 'rotate(45deg)'
        }}
      />
      
      <motion.div
        animate={{ 
          y: [0, 40, 0],
          rotate: [0, -180, -360]
        }}
        transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
        className="absolute bottom-40 left-10 w-40 h-40 bg-indigo-400/20 backdrop-blur-sm rounded-full"
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 min-h-screen flex items-center">
        <div className="grid lg:grid-cols-2 gap-12 items-center w-full">
          
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-white space-y-8 z-10"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
            >
              <div className="inline-flex items-center gap-2 px-5 py-2 bg-white/20 backdrop-blur-md rounded-full border border-white/30">
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="w-2 h-2 bg-green-400 rounded-full"
                />
                <span className="text-sm font-semibold">500+ Live Sessions Daily</span>
              </div>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="text-6xl md:text-7xl font-black leading-tight"
            >
              Transform
              <span className="block bg-gradient-to-r from-blue-200 to-white bg-clip-text text-transparent">
                Presentations
              </span>
              Into Experiences
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="text-xl text-blue-100 leading-relaxed max-w-lg"
            >
              Host stunning webinars that captivate audiences worldwide. 
              Interactive tools, seamless streaming, instant analytics.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-wrap gap-4"
            >
              <Button
                onClick={onBrowseWebinars}
                size="lg"
                className="bg-white text-blue-600 hover:bg-blue-50 px-10 py-5 text-lg font-bold shadow-2xl"
                iconName="Zap"
                iconPosition="left"
              >
                Start Free Trial
              </Button>
              <Button
                onClick={onBecomeInstructor}
                variant="outline"
                size="lg"
                className="border-2 border-white text-white hover:bg-white/10 backdrop-blur-sm px-10 py-5 text-lg font-bold"
                iconName="PlayCircle"
                iconPosition="left"
              >
                Watch Demo
              </Button>
            </motion.div>

            {/* Stats Bar */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="flex gap-8 pt-4"
            >
              {[
                { value: '50K+', label: 'Users' },
                { value: '1M+', label: 'Webinars' },
                { value: '4.9/5', label: 'Rating' }
              ].map((stat, idx) => (
                <div key={idx}>
                  <div className="text-3xl font-black">{stat.value}</div>
                  <div className="text-sm text-blue-200">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right Side - Stacked Cards */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative h-[600px] z-10"
          >
            {/* Card Stack */}
            {[
              { delay: 0.4, rotate: -6, z: 1, image: 'https://images.unsplash.com/photo-1587825140708-dfaf72ae4b04?w=400' },
              { delay: 0.5, rotate: -3, z: 2, image: 'https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?w=400' },
              { delay: 0.6, rotate: 0, z: 3, image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=400' }
            ].map((card, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 50, rotate: 0 }}
                animate={{ opacity: 1, y: 0, rotate: card.rotate }}
                transition={{ delay: card.delay, duration: 0.6 }}
                whileHover={{ rotate: 0, scale: 1.05, zIndex: 10 }}
                className="absolute inset-0 bg-white rounded-3xl shadow-2xl overflow-hidden cursor-pointer"
                style={{ 
                  zIndex: card.z,
                  top: `${idx * 20}px`,
                  left: `${idx * 15}px`
                }}
              >
                <Image
                  src={card.image}
                  alt="Webinar dashboard"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 via-transparent to-transparent" />
                
                {/* Card Content */}
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                      <Icon name={['Video', 'Users', 'MessageSquare'][idx]} size={20} className="text-white" />
                    </div>
                    <div className="text-white">
                      <div className="font-bold">{['Live Streaming', 'Audience Panel', 'Live Chat'][idx]}</div>
                      <div className="text-xs text-gray-200">Feature {idx + 1}</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
